import { useRef, useState } from "react";
import { act } from "@/components/common";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ImageUp, Trash2, Loader2 } from "lucide-react";

export const logoUrl = (company) => (company?.logo ? `${API}/public/companies/${company.id}/logo?v=${encodeURIComponent(company.logo.uploaded_at || "")}`
  : /digivate/i.test(company?.name || "") && company?.id ? `${API}/public/companies/${company.id}/logo` : null);

export function LogoUploader({ company, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = logoUrl(company);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    const r = await act(() => api.post(`/companies/${company.id}/logo`, fd), "Logo updated");
    setBusy(false);
    if (r) onChange?.();
  };
  const remove = async () => { if (window.confirm("Remove logo?") && (await act(() => api.delete(`/companies/${company.id}/logo`), "Logo removed"))) onChange?.(); };
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center" data-testid="logo-uploader">
      <div className="flex h-24 w-56 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
        {url ? <img src={url} alt="Company logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <span className="text-xs text-slate-400">No logo uploaded</span>}
      </div>
      <div className="space-y-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="logo-file-input" />
        <div className="flex gap-2">
          <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="logo-upload-button">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImageUp className="mr-1.5 h-4 w-4" />}{url ? "Replace logo" : "Upload logo"}</Button>
          {url && <Button variant="outline" onClick={remove} data-testid="logo-remove-button"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        </div>
        <p className="text-xs text-slate-500">PNG, JPG or WEBP · max 2 MB · a wide logo on a light background works best.</p>
      </div>
    </div>
  );
}
